import useGuardedRealtimeList, { readListRows } from '../../realtime/useGuardedRealtimeList';
import { fetchBackgroundData } from '../../redux/api/portalApi';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Form, InputGroup, Badge, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { fetchData, postData, patchData } from '../../redux/slices/apiCallSlice';
import './MechanicalReference.css';

const hasShippedGeneralProjectStatus = (job) => (
    String(job?.__generalProjectJobStatus || '').trim().toLowerCase() === 'shipped'
);

const MECHANICAL_LOG_DATE_FIELDS = new Set(['MechanicalEngineerDateEntered']);
const MECHANICAL_LOG_NUMERIC_FIELDS = new Set([
    'WaterFeatureMotorHP',
    'TransferMotorHP',
    'RecircMotorHP',
    'TrashMotorHP',
    'SumpMotorHP',
    'DischargeFilterQty'
]);
const MECHANICAL_LOG_FIELDS = [
    'MechanicalEngineerInitials',
    'MechanicalEngineerDateEntered',
    'StationType',
    'EngineeringDrawingNumber',
    'JobNumber',
    'JobName',
    'StationModelNumber',
    'Flow',
    'Pressure',
    'Voltage',
    'Phase',
    'HCType',
    'StandarJob',
    'PrivateLabel',
    'MainJockeyQty',
    'PumpOrientation',
    'WetWellDiameterLength',
    'WetWellWidth',
    'WetWellDepth',
    'BaseLength',
    'BaseWidth',
    'BaseHeight',
    'BaseMaterial',
    'PipingMaterial',
    'PipeColor',
    'StationColor',
    'StationEnclosureType',
    'ElectricalPanelSize',
    'PanelOnOffSkid',
    'StationHeater',
    'TransformerStand',
    'MainPump',
    'MainMotorHP',
    'JockeyPump',
    'JockeyMotorHP',
    'SustainPump',
    'SustainMotorHP',
    'WaterFeaturePump',
    'WaterFeatureMotorHP',
    'TransferPump',
    'TransferMotorHP',
    'RecircPump',
    'RecircMotorHP',
    'TrashPump',
    'TrashMotorHP',
    'SumpPump',
    'SumpMotorHP',
    'DischargeFilter',
    'DischargeFilterQty',
    'FilterOrientation',
    'DischargeStrainer',
    'InletFiltration',
    'InletStrainer',
    'ROSystem',
    'UVSystem',
    'PRV',
    'EBV',
    'FlowMeter',
    'Fertigation',
    'SSFasteners',
    'FBE',
    'SolidWorks',
    'WaterTesting',
    'Prime',
    'RPZ',
    'SuctionLine',
    'Note'
];
const MECHANICAL_LOG_EDITABLE_FIELDS = [...MECHANICAL_LOG_FIELDS];
const LOG_FIELD_TO_TABLE_KEY = {
    MechanicalEngineerInitials: 'Engineer Initials',
    MechanicalEngineerDateEntered: 'Engineer Date Entered',
    StationType: 'Station Type',
    EngineeringDrawingNumber: 'Drawing Number',
    JobNumber: 'Job Number',
    JobName: 'Job Name',
    StationModelNumber: 'Station Model Number',
    Flow: 'Flow',
    Pressure: 'Pressure',
    Voltage: 'Voltage',
    Phase: 'Phase',
    HCType: 'HC TYPE',
    StandarJob: 'Standard Job',
    PrivateLabel: 'Private Label',
    MainJockeyQty: '# of pumps',
    PumpOrientation: 'PUMP ORIENTAION',
    WetWellDiameterLength: 'Well Diameter or Length',
    WetWellWidth: 'Wet Well Width',
    WetWellDepth: 'Wet Well Depth',
    BaseLength: 'Base length',
    BaseWidth: 'Base width',
    BaseHeight: 'Base Height',
    BaseMaterial: 'Base Material',
    PipingMaterial: 'Piping Material',
    PipeColor: 'Pipe Color',
    StationColor: 'Station Color',
    StationEnclosureType: 'Enclosure Type',
    ElectricalPanelSize: 'Panel (L X W X H)',
    PanelOnOffSkid: 'Cabinet on/off Skid',
    StationHeater: 'Station Heater',
    TransformerStand: 'Transformer Stand',
    MainPump: 'Main pump Model Number',
    MainMotorHP: 'Main Motor (HP)',
    JockeyPump: 'Jockey Pump',
    JockeyMotorHP: 'Jockey Motor (HP)',
    SustainPump: 'Pressure Maintenance Pump',
    SustainMotorHP: 'Pressure Maintenance Motor (HP)',
    WaterFeaturePump: 'Water Feature',
    WaterFeatureMotorHP: 'Water Feature Motor (HP)',
    TransferPump: 'Transfer Pump',
    TransferMotorHP: 'Transfer Motor (HP)',
    RecircPump: 'Recirc Pump',
    RecircMotorHP: 'Recirc Motor (HP)',
    TrashPump: 'Trash Pump',
    TrashMotorHP: 'Trash Motor (HP)',
    SumpPump: 'Sump Pump',
    SumpMotorHP: 'Sump Motor (HP)',
    DischargeFilter: 'Discharge Filter',
    DischargeFilterQty: '# of Filters',
    FilterOrientation: 'Filter Orientation',
    DischargeStrainer: 'Discharge Strainer',
    InletFiltration: 'Inlet Filtration',
    InletStrainer: 'Inlet Strainer',
    ROSystem: 'RO System',
    UVSystem: 'UV System',
    PRV: 'Prv',
    EBV: 'EBV',
    FlowMeter: 'Flow meter',
    Fertigation: 'Fertigation',
    SSFasteners: 'SS Fastener',
    FBE: 'FBE',
    SolidWorks: 'Solidworks',
    WaterTesting: 'Water Testing',
    Prime: 'Prime',
    RPZ: 'RPZ',
    SuctionLine: 'Suction Line',
    Note: 'NOTES'
};
const TABLE_KEY_TO_LOG_FIELD = Object.fromEntries(
    Object.entries(LOG_FIELD_TO_TABLE_KEY).map(([fieldName, columnKey]) => [columnKey, fieldName])
);
const combineSourceValues = (...values) => {
    const filledValues = values
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map((value) => String(value).trim())
        .filter((value) => value !== '');

    return filledValues.length > 0 ? filledValues.join(' ') : null;
};
const combineRecordValues = (records, valueSelector) => {
    const filledValues = (records || [])
        .map(valueSelector)
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map((value) => String(value).trim())
        .filter((value) => value !== '');
    const uniqueValues = [...new Set(filledValues)];

    return uniqueValues.length > 0 ? uniqueValues.join('; ') : null;
};

// Advanced Filter Row Component
const AdvancedFilterRow = ({ filter, index, allFields, getUniqueValues, onUpdate, onRemove }) => {
    const [fieldSuggestions, setFieldSuggestions] = useState([]);
    const [valueSuggestions, setValueSuggestions] = useState([]);
    const [showFieldSuggestions, setShowFieldSuggestions] = useState(false);
    const [showValueSuggestions, setShowValueSuggestions] = useState(false);
    
    // Filter field suggestions based on search
    useEffect(() => {
        if (filter.fieldSearch) {
            const search = filter.fieldSearch.toLowerCase();
            const suggestions = allFields.filter(field => 
                field.toLowerCase().includes(search)
            );
            setFieldSuggestions(suggestions);
        } else {
            setFieldSuggestions(allFields);
        }
    }, [filter.fieldSearch, allFields]);
    
    // Get value suggestions when field is selected
    useEffect(() => {
        if (filter.field) {
            const uniqueValues = getUniqueValues(filter.field);
            if (filter.value) {
                const search = filter.value.toLowerCase();
                setValueSuggestions(uniqueValues.filter(val => 
                    val.toLowerCase().includes(search)
                ));
            } else {
                setValueSuggestions(uniqueValues);
            }
        }
    }, [filter.field, filter.value, getUniqueValues]);
    
    const selectField = (field) => {
        onUpdate({ field, fieldSearch: field, value: '' });
        setShowFieldSuggestions(false);
    };
    
    const selectValue = (value) => {
        onUpdate({ value });
        setShowValueSuggestions(false);
    };
    
    return (
        <div className="filter-row">
            <span className="filter-index">{index + 1}.</span>
            
            <div className="filter-field-selector position-relative">
                <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Type to search fields..."
                    value={filter.fieldSearch || filter.field}
                    onChange={(e) => {
                        onUpdate({ fieldSearch: e.target.value, field: '' });
                        setShowFieldSuggestions(true);
                    }}
                    onFocus={() => setShowFieldSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowFieldSuggestions(false), 200)}
                />
                {showFieldSuggestions && fieldSuggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                        {fieldSuggestions.slice(0, 10).map(field => (
                            <div
                                key={field}
                                className="suggestion-item"
                                onClick={() => selectField(field)}
                            >
                                {field}
                            </div>
                        ))}
                        {fieldSuggestions.length > 10 && (
                            <div className="suggestion-item text-muted fst-italic">
                                +{fieldSuggestions.length - 10} more...
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <span className="filter-operator">contains</span>
            
            <div className="filter-value-input position-relative">
                <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Enter value..."
                    value={filter.value}
                    onChange={(e) => {
                        onUpdate({ value: e.target.value });
                        setShowValueSuggestions(true);
                    }}
                    onFocus={() => setShowValueSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowValueSuggestions(false), 200)}
                    disabled={!filter.field}
                />
                {showValueSuggestions && filter.field && valueSuggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                        {valueSuggestions.slice(0, 10).map(value => (
                            <div
                                key={value}
                                className="suggestion-item"
                                onClick={() => selectValue(value)}
                            >
                                {value}
                            </div>
                        ))}
                        {valueSuggestions.length > 10 && (
                            <div className="suggestion-item text-muted fst-italic">
                                +{valueSuggestions.length - 10} more...
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <button
                className="btn btn-sm btn-outline-danger"
                onClick={onRemove}
                title="Remove filter"
            >
                <i className="bi bi-trash"></i>
            </button>
        </div>
    );
};

const MechanicalReference = () => {
    const dispatch = useDispatch();
    const apiUrls = useSelector(selectApiUrls);

    const isEmptyValue = (value) => value === null || value === undefined || value === '';
    const preferExistingValue = (primaryValue, fallbackValue) => {
        return isEmptyValue(primaryValue) ? fallbackValue : primaryValue;
    };
    const resolveFieldValue = (primaryValue, fallbackValue) => {
        if (!isEmptyValue(primaryValue)) {
            return { value: primaryValue, source: 'external' };
        }

        if (!isEmptyValue(fallbackValue)) {
            return { value: fallbackValue, source: 'log' };
        }

        return { value: null, source: 'empty' };
    };
    const resolveLogOnlyFieldValue = (value) => {
        if (!isEmptyValue(value)) {
            return { value, source: 'log' };
        }

        return { value: null, source: 'empty' };
    };
    // State for job data
    const [jobData, setJobData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Column mapping configuration from CSV
    const columnMappings = [
        { column: "Engineer", table: "GeneralProjectInfo", field: "MechanicalEngineer", notes: "" },
        { column: "Engineer Initials", table: "MechanicalReferenceLog", field: "MechanicalEngineerInitials", notes: "" },
        { column: "Engineer Date Entered", table: "MechanicalReferenceLog", field: "MechanicalEngineerDateEntered", notes: "" },
        { column: "Station Type", table: "GeneralStationInfo", field: "StationType", notes: "" },
        { column: "Drawing Number", table: "GeneralProjectInfo", field: "DrawingNumber", notes: "" },
        { column: "Drawing Type", table: "GeneralProjectInfo", field: "DrawingType", notes: "" },
        { column: "Job Number", table: "GeneralProjectInfo", field: "JobNumber", notes: "" },
        { column: "Job Name", table: "GeneralProjectInfo", field: "ProjectName", notes: "" },
        { column: "Station Model Number", table: "GeneralProjectInfo", field: "ModelNumber", notes: "" },
        { column: "Flow", table: "GeneralStationInfo", field: "TotalGPM", notes: "" },
        { column: "Pressure", table: "GeneralStationInfo", field: "DischargePSI", notes: "" },
        { column: "Voltage", table: "GeneralStationInfo", field: "Voltage", notes: "" },
        { column: "Phase", table: "MechanicalReferenceLog", field: "Phase", notes: "" },
        { column: "HC TYPE", table: "GeneralStationInfo", field: "StationConfiguration", notes: "" },
        { column: "Standard Job", table: "MechanicalReferenceLog", field: "StandarJob", notes: "" },
        { column: "Private Label", table: "MechanicalReferenceLog", field: "PrivateLabel", notes: "" },
        { column: "# of pumps", table: "PumpDetails", field: "Quantity", notes: "PumpID = Main" },
        { column: "PUMP ORIENTAION", table: "MechanicalReferenceLog", field: "PumpOrientation", notes: "" },
        { column: "Well Diameter or Length", table: "MechanicalReferenceLog", field: "WetWellDiameterLength", notes: "" },
        { column: "Wet Well Width", table: "MechanicalReferenceLog", field: "WetWellWidth", notes: "" },
        { column: "Wet Well Depth", table: "GeneralStationInfo", field: "WetWellDepth", notes: "" },
        { column: "Base length", table: "StationEnclosure", field: "BaseLength", notes: "" },
        { column: "Base width", table: "StationEnclosure", field: "BaseWidth", notes: "" },
        { column: "Base Height", table: "MechanicalReferenceLog", field: "BaseHeight", notes: "" },
        { column: "Base Material", table: "MechanicalReferenceLog", field: "BaseMaterial", notes: "" },
        { column: "Piping Material", table: "PumpManifoldPiping", field: "PipingMaterial", notes: "" },
        { column: "Pipe Color", table: "PumpManifoldPiping", field: "PipeColor", notes: "" },
        { column: "Station Color", table: "StationEnclosure", field: "EnclosurePaintColor", notes: "" },
        { column: "Enclosure Type", table: "StationEnclosure", field: "EnclosureType", notes: "" },
        { column: "Panel (L X W X H)", table: "ElectricalOptions", field: "ElectricalEnclosureActualSize", notes: "" },
        { column: "Cabinet on/off Skid", table: "GeneralStationInfo", field: "ElectricalEnclosureOnSkid", notes: "" },
        { column: "Station Heater", table: "ElectricalOptions", field: "StationHeating", notes: "" },
        { column: "Transformer Stand", table: "RegulatedPowerSupply/ UnregulatedPowerSupply", field: "SupplyType", notes: "" },
        { column: "Main pump Model Number", table: "PumpDetails", field: "PumpManufacturerModelNumber", notes: "PumpID = Main" },
        { column: "Main Motor (HP)", table: "PumpDetails", field: "MotorHP", notes: "PumpID = Main" },
        { column: "Jockey Pump", table: "PumpDetails", field: "PumpManufacturerModelNumber", notes: "PumpID = Jockey" },
        { column: "Jockey Motor (HP)", table: "PumpDetails", field: "MotorHP", notes: "PumpID = Jockey" },
        { column: "Pressure Maintenance Pump", table: "PumpDetails", field: "PumpManufacturerModelNumber", notes: "PumpID = PM" },
        { column: "Pressure Maintenance Motor (HP)", table: "PumpDetails", field: "MotorHP", notes: "PumpID = PM" },
        { column: "Water Feature", table: "PumpDetails", field: "PumpManufacturerModelNumber", notes: "PumpID = WaterFeature" },
        { column: "Water Feature Motor (HP)", table: "MechanicalReferenceLog", field: "WaterFeatureMotorHP", notes: "" },
        { column: "Transfer Pump", table: "MechanicalReferenceLog", field: "TransferPump", notes: "" },
        { column: "Transfer Motor (HP)", table: "MechanicalReferenceLog", field: "TransferMotorHP", notes: "" },
        { column: "Recirc Pump", table: "MechanicalReferenceLog", field: "RecircPump", notes: "" },
        { column: "Recirc Motor (HP)", table: "MechanicalReferenceLog", field: "RecircMotorHP", notes: "" },
        { column: "Trash Pump", table: "MechanicalReferenceLog", field: "TrashPump", notes: "" },
        { column: "Trash Motor (HP)", table: "MechanicalReferenceLog", field: "TrashMotorHP", notes: "" },
        { column: "Sump Pump", table: "MechanicalReferenceLog", field: "SumpPump", notes: "" },
        { column: "Sump Motor (HP)", table: "MechanicalReferenceLog", field: "SumpMotorHP", notes: "" },
        { column: "Discharge Filter", table: "MechanicalReferenceLog", field: "DischargeFilter", notes: "" },
        { column: "# of Filters", table: "MechanicalReferenceLog", field: "DischargeFilterQty", notes: "" },
        { column: "Filter Orientation", table: "MechanicalReferenceLog", field: "FilterOrientation", notes: "" },
        { column: "Discharge Strainer", table: "MechanicalReferenceLog", field: "DischargeStrainer", notes: "" },
        { column: "Inlet Filtration", table: "MechanicalReferenceLog", field: "InletFiltration", notes: "" },
        { column: "Inlet Strainer", table: "MechanicalReferenceLog", field: "InletStrainer", notes: "" },
        { column: "RO System", table: "MechanicalReferenceLog", field: "ROSystem", notes: "" },
        { column: "UV System", table: "MechanicalReferenceLog", field: "UVSystem", notes: "" },
        { column: "Prv", table: "PumpManifoldPiping", field: "PRVSize", notes: "" },
        { column: "EBV", table: "PumpDetails", field: "EBV", notes: "PumpID = Main" },
        { column: "Flow meter", table: "PumpManifoldPiping", field: "FlowMeterType", notes: "" },
        { column: "Fertigation", table: "Fertigation", field: "WatertronicsFertigation", notes: "" },
        { column: "SS Fastener", table: "PumpManifoldPiping", field: "StationHardware", notes: "" },
        { column: "FBE", table: "PumpManifoldPiping", field: "PipeCoating", notes: "" },
        { column: "Solidworks", table: "GeneralProjectInfo", field: "DrawingType", notes: "" },
        { column: "Water Testing", table: "MechanicalReferenceLog", field: "WaterTesting", notes: "" },
        { column: "Prime", table: "GeneralStationInfo", field: "PrimeSystem", notes: "" },
        { column: "RPZ", table: "HCRequirements", field: "BackflowPreventer", notes: "" },
        { column: "Suction Line", table: "MechanicalReferenceLog", field: "SuctionLine", notes: "" },
        { column: "NOTES", table: "MechanicalReferenceLog", field: "Note", notes: "" }
    ];

    // State for search and filtering
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'ProjectID', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [compactView, setCompactView] = useState(false);
    const [showShippedOnly, setShowShippedOnly] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState([]);
    const [nextFilterId, setNextFilterId] = useState(1);
    const [editingCell, setEditingCell] = useState({ rowKey: null, fieldName: null });
    const [editingJob, setEditingJob] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [originalValue, setOriginalValue] = useState('');
    const [isSavingCell, setIsSavingCell] = useState(false);
    const [inlineEditError, setInlineEditError] = useState('');

    const toDateInputValue = (value) => {
        if (!value) {
            return '';
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return '';
        }

        return parsed.toISOString().split('T')[0];
    };

    const normalizePatchComparableValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (value instanceof Date) {
            return value.toISOString().split('T')[0];
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();

            if (trimmedValue === '') {
                return null;
            }

            if (trimmedValue === 'true') {
                return true;
            }

            if (trimmedValue === 'false') {
                return false;
            }

            if (/^\d{4}-\d{2}-\d{2}/.test(trimmedValue)) {
                const parsedDate = Date.parse(trimmedValue);
                if (!Number.isNaN(parsedDate)) {
                    return new Date(parsedDate).toISOString().split('T')[0];
                }
            }

            return trimmedValue;
        }

        if (typeof value === 'number' && Number.isNaN(value)) {
            return null;
        }

        return value;
    };

    const arePatchValuesEquivalent = (leftValue, rightValue) => (
        normalizePatchComparableValue(leftValue) === normalizePatchComparableValue(rightValue)
    );

    const getFieldLabel = (fieldName) => {
        if (fieldName === 'PRV') {
            return 'PRV';
        }

        if (fieldName === 'ROSystem') {
            return 'RO System';
        }

        if (fieldName === 'UVSystem') {
            return 'UV System';
        }

        if (fieldName === 'EBV') {
            return 'EBV';
        }

        return fieldName
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/\bHp\b/g, 'HP')
            .trim();
    };

    const getContextFieldValue = (job, fieldName) => {
        const baselineLogRecord = job?.MechanicalReferenceLogRecord || {};
        const baselineValue = baselineLogRecord?.[fieldName];

        if (!isEmptyValue(baselineValue)) {
            return MECHANICAL_LOG_DATE_FIELDS.has(fieldName) ? toDateInputValue(baselineValue) : baselineValue;
        }

        const tableKey = LOG_FIELD_TO_TABLE_KEY[fieldName];
        const tableValue = tableKey ? job?.[tableKey] : null;
        return MECHANICAL_LOG_DATE_FIELDS.has(fieldName) ? toDateInputValue(tableValue) : (tableValue ?? '');
    };
    const getEditableFieldsForJob = (job) => {
        if (isEmptyValue(job?.ProjectID)) {
            return MECHANICAL_LOG_EDITABLE_FIELDS;
        }

        const fieldSources = job?.MechanicalReferenceFieldSources || {};
        return MECHANICAL_LOG_EDITABLE_FIELDS.filter((fieldName) => fieldSources[fieldName] !== 'external');
    };
    const isFieldEditableForJob = (job, fieldName) => getEditableFieldsForJob(job).includes(fieldName);
    
    // Fetch all necessary data
    const fetchAllData = useCallback(async ({ snapshotOnly = false, signal } = {}) => {
        if (!apiUrls.generalProject) {
            return;
        }

            if (!snapshotOnly) { setLoading(true); setError(null); }
            const read = async url => snapshotOnly ? readListRows(await dispatch(fetchBackgroundData({ url, signal }))) : dispatch(fetchData({ url })).unwrap();
            
            try {
                // Fetch all required API data using centralized API URLs from apiUrlSlice
                const [generalProject, generalStation, pumpDetails, stationEnclosure, 
                       skidConstruction, pumpManifold, electrical, regulatedPower, 
                       unregulatedPower, fertigation, hcRequirements, inletScreen,
                       dischargeFilter, dischargeStrainer, inletStrainer, mechanicalReferenceLog] = await Promise.all([
                    read(apiUrls.generalProject),
                    read(apiUrls.generalStation),
                    read(apiUrls.pumpDetails),
                    read(apiUrls.stationEnclosure),
                    read(apiUrls.skidConstruction),
                    read(apiUrls.pumpManifoldPiping),
                    read(apiUrls.electricalOptions),
                    read(apiUrls.regulatedPower),
                    read(apiUrls.unregulatedPower),
                    read(apiUrls.fertigation),
                    read(apiUrls.hcRequirements),
                    read(apiUrls.inletScreen),
                    read(apiUrls.dischargeFilter),
                    read(apiUrls.dischargeStrainer),
                    read(apiUrls.inletStrainer),
                    read(apiUrls.mechanicalReferenceLog)
                ]);

                const mechanicalLogByProjectId = new Map();
                const mechanicalLogByJobNumber = new Map();
                (mechanicalReferenceLog || []).forEach((logEntry) => {
                    if (logEntry?.ProjectID !== null && logEntry?.ProjectID !== undefined && logEntry?.ProjectID !== '') {
                        const projectKey = String(logEntry.ProjectID);
                        const current = mechanicalLogByProjectId.get(projectKey);
                        if (!current || (Number(logEntry.ID) || 0) > (Number(current.ID) || 0)) {
                            mechanicalLogByProjectId.set(projectKey, logEntry);
                        }
                    }

                    if (logEntry?.JobNumber) {
                        const jobKey = String(logEntry.JobNumber);
                        const current = mechanicalLogByJobNumber.get(jobKey);
                        if (!current || (Number(logEntry.ID) || 0) > (Number(current.ID) || 0)) {
                            mechanicalLogByJobNumber.set(jobKey, logEntry);
                        }
                    }
                });

                const latestMechanicalEntries = [];
                const seenMechanicalIds = new Set();

                mechanicalLogByProjectId.forEach((entry) => {
                    if (entry?.ID !== undefined && entry?.ID !== null && !seenMechanicalIds.has(entry.ID)) {
                        seenMechanicalIds.add(entry.ID);
                        latestMechanicalEntries.push(entry);
                    }
                });

                mechanicalLogByJobNumber.forEach((entry) => {
                    if (entry?.ID !== undefined && entry?.ID !== null && !seenMechanicalIds.has(entry.ID)) {
                        seenMechanicalIds.add(entry.ID);
                        latestMechanicalEntries.push(entry);
                    }
                });
                
                // Filter out ScheduleOnly and BookingLogOnly jobs and combine all data by ProjectID
                const eligibleProjects = generalProject
                    .filter(project => !project.ScheduleOnly && !project.BookingLogOnly);

                const existingProjectIds = new Set(
                    eligibleProjects
                        .map(project => project?.ID)
                        .filter(id => id !== null && id !== undefined && id !== '')
                        .map(id => String(id))
                );

                const existingJobNumbers = new Set(
                    eligibleProjects
                        .map(project => project?.JobNumber)
                        .filter(jobNumber => jobNumber !== null && jobNumber !== undefined && jobNumber !== '')
                        .map(jobNumber => String(jobNumber))
                );

                const combinedData = eligibleProjects
                    .map(project => {
                    const projectId = project.ID; // GeneralProjectInfo primary key is ID
                    
                    // Find child records by matching their ProjectID to parent's ID
                    const station = generalStation?.find(s => s.ProjectID === projectId);
                    const pumps = pumpDetails?.filter(p => p.ProjectID === projectId) || [];
                    const mainPump = pumps.find(p => p.PumpID === 'Main');
                    const jockeyPump = pumps.find(p => p.PumpID === 'Jockey');
                    const sustainPump = pumps.find(p => ['pm', 'pressure maintenance', 'sustain'].includes(String(p.PumpID ?? '').trim().toLowerCase()));
                    const waterFeature = pumps.find(p => p.PumpID === 'WaterFeature');
                    const enclosure = stationEnclosure?.find(e => e.ProjectID === projectId);
                    const skid = skidConstruction?.find(s => s.ProjectID === projectId);
                    const piping = pumpManifold?.find(p => p.ProjectID === projectId);
                    const elec = electrical?.find(e => e.ProjectID === projectId);
                    const regPower = regulatedPower?.find(r => r.ProjectID === projectId);
                    const unregPower = unregulatedPower?.find(u => u.ProjectID === projectId);
                    const fert = fertigation?.find(f => f.ProjectID === projectId);
                    const hc = hcRequirements?.find(h => h.ProjectID === projectId);
                    const inletFiltrationDetails = inletScreen?.filter(f => f.ProjectID === projectId) || [];
                    const dischargeFilterDetails = dischargeFilter?.filter(f => f.ProjectID === projectId) || [];
                    const dischargeStrainerDetails = dischargeStrainer?.filter(s => s.ProjectID === projectId) || [];
                    const inletStrainerDetails = inletStrainer?.filter(s => s.ProjectID === projectId) || [];
                    const mechLog = mechanicalLogByProjectId.get(String(projectId)) || mechanicalLogByJobNumber.get(String(project?.JobNumber || ''));
                    const fieldValues = {
                        MechanicalEngineerInitials: resolveLogOnlyFieldValue(mechLog?.MechanicalEngineerInitials),
                        MechanicalEngineerDateEntered: resolveLogOnlyFieldValue(mechLog?.MechanicalEngineerDateEntered),
                        StationType: resolveFieldValue(station?.StationType, mechLog?.StationType),
                        EngineeringDrawingNumber: resolveFieldValue(project.DrawingNumber, mechLog?.EngineeringDrawingNumber),
                        JobNumber: resolveFieldValue(project.JobNumber, mechLog?.JobNumber),
                        JobName: resolveFieldValue(project.ProjectName, mechLog?.JobName),
                        StationModelNumber: resolveFieldValue(project.ModelNumber, mechLog?.StationModelNumber),
                        Flow: resolveFieldValue(station?.TotalGPM, mechLog?.Flow),
                        Pressure: resolveFieldValue(station?.DischargePSI, mechLog?.Pressure),
                        Voltage: resolveFieldValue(station?.Voltage, mechLog?.Voltage),
                        Phase: resolveLogOnlyFieldValue(mechLog?.Phase),
                        HCType: resolveFieldValue(station?.StationConfiguration, mechLog?.HCType),
                        StandarJob: resolveLogOnlyFieldValue(mechLog?.StandarJob),
                        PrivateLabel: resolveLogOnlyFieldValue(mechLog?.PrivateLabel),
                        MainJockeyQty: resolveFieldValue(mainPump?.Quantity, mechLog?.MainJockeyQty),
                        PumpOrientation: resolveLogOnlyFieldValue(mechLog?.PumpOrientation),
                        WetWellDiameterLength: resolveLogOnlyFieldValue(mechLog?.WetWellDiameterLength),
                        WetWellWidth: resolveLogOnlyFieldValue(mechLog?.WetWellWidth),
                        WetWellDepth: resolveFieldValue(station?.WetWellDepth, mechLog?.WetWellDepth),
                        BaseLength: resolveFieldValue(enclosure?.BaseLength, mechLog?.BaseLength),
                        BaseWidth: resolveFieldValue(enclosure?.BaseWidth, mechLog?.BaseWidth),
                        BaseHeight: resolveFieldValue(skid?.SkidConstruction, mechLog?.BaseHeight),
                        BaseMaterial: resolveFieldValue(skid?.SkidConstruction, mechLog?.BaseMaterial),
                        PipingMaterial: resolveFieldValue(piping?.PipingMaterial, mechLog?.PipingMaterial),
                        PipeColor: resolveFieldValue(piping?.PipeColor, mechLog?.PipeColor),
                        StationColor: resolveFieldValue(enclosure?.EnclosurePaintColor, mechLog?.StationColor),
                        StationEnclosureType: resolveFieldValue(enclosure?.EnclosureType, mechLog?.StationEnclosureType),
                        ElectricalPanelSize: resolveFieldValue(elec?.ElectricalEnclosureActualSize, mechLog?.ElectricalPanelSize),
                        PanelOnOffSkid: resolveFieldValue(station?.ElectricalEnclosureOnSkid, mechLog?.PanelOnOffSkid),
                        StationHeater: resolveFieldValue(elec?.StationHeating, mechLog?.StationHeater),
                        TransformerStand: resolveFieldValue(regPower?.SupplyType || unregPower?.SupplyType, mechLog?.TransformerStand),
                        MainPump: resolveFieldValue(mainPump?.PumpManufacturerModelNumber, mechLog?.MainPump),
                        MainMotorHP: resolveFieldValue(mainPump?.MotorHP, mechLog?.MainMotorHP),
                        JockeyPump: resolveFieldValue(jockeyPump?.PumpManufacturerModelNumber, mechLog?.JockeyPump),
                        JockeyMotorHP: resolveFieldValue(jockeyPump?.MotorHP, mechLog?.JockeyMotorHP),
                        SustainPump: resolveFieldValue(sustainPump?.PumpManufacturerModelNumber, mechLog?.SustainPump),
                        SustainMotorHP: resolveFieldValue(sustainPump?.MotorHP, mechLog?.SustainMotorHP),
                        WaterFeaturePump: resolveFieldValue(waterFeature?.PumpManufacturerModelNumber, mechLog?.WaterFeaturePump),
                        WaterFeatureMotorHP: resolveLogOnlyFieldValue(mechLog?.WaterFeatureMotorHP),
                        TransferPump: resolveLogOnlyFieldValue(mechLog?.TransferPump),
                        TransferMotorHP: resolveLogOnlyFieldValue(mechLog?.TransferMotorHP),
                        RecircPump: resolveLogOnlyFieldValue(mechLog?.RecircPump),
                        RecircMotorHP: resolveLogOnlyFieldValue(mechLog?.RecircMotorHP),
                        TrashPump: resolveLogOnlyFieldValue(mechLog?.TrashPump),
                        TrashMotorHP: resolveLogOnlyFieldValue(mechLog?.TrashMotorHP),
                        SumpPump: resolveLogOnlyFieldValue(mechLog?.SumpPump),
                        SumpMotorHP: resolveLogOnlyFieldValue(mechLog?.SumpMotorHP),
                        DischargeFilter: resolveFieldValue(
                            combineRecordValues(
                                dischargeFilterDetails,
                                (filter) => combineSourceValues(filter?.Manufacturer, filter?.Type, filter?.Model)
                            ),
                            mechLog?.DischargeFilter
                        ),
                        DischargeFilterQty: resolveFieldValue(
                            combineRecordValues(dischargeFilterDetails, (filter) => filter?.Quantity),
                            mechLog?.DischargeFilterQty
                        ),
                        FilterOrientation: resolveLogOnlyFieldValue(mechLog?.FilterOrientation),
                        DischargeStrainer: resolveFieldValue(
                            combineRecordValues(
                                dischargeStrainerDetails,
                                (strainer) => combineSourceValues(strainer?.Manufacturer, strainer?.TypeSize)
                            ),
                            mechLog?.DischargeStrainer
                        ),
                        InletFiltration: resolveFieldValue(
                            combineRecordValues(
                                inletFiltrationDetails,
                                (filter) => combineSourceValues(filter?.FilterManufacturer, filter?.InletFilterType, filter?.InletFilterModel)
                            ),
                            mechLog?.InletFiltration
                        ),
                        InletStrainer: resolveFieldValue(
                            combineRecordValues(
                                inletStrainerDetails,
                                (strainer) => combineSourceValues(strainer?.Manufacturer, strainer?.TypeSize)
                            ),
                            mechLog?.InletStrainer
                        ),
                        ROSystem: resolveLogOnlyFieldValue(mechLog?.ROSystem),
                        UVSystem: resolveLogOnlyFieldValue(mechLog?.UVSystem),
                        PRV: resolveFieldValue(piping?.PRVSize, mechLog?.PRV),
                        EBV: resolveFieldValue(mainPump?.EBV, mechLog?.EBV),
                        FlowMeter: resolveFieldValue(piping?.FlowMeterType, mechLog?.FlowMeter),
                        Fertigation: resolveFieldValue(fert?.WatertronicsFertigation, mechLog?.Fertigation),
                        SSFasteners: resolveFieldValue(piping?.StationHardware, mechLog?.SSFasteners),
                        FBE: resolveFieldValue(piping?.PipeCoating, mechLog?.FBE),
                        SolidWorks: resolveFieldValue(project.DrawingType, mechLog?.SolidWorks),
                        WaterTesting: resolveLogOnlyFieldValue(mechLog?.WaterTesting),
                        Prime: resolveFieldValue(station?.PrimeSystem, mechLog?.Prime),
                        RPZ: resolveFieldValue(hc?.BackflowPreventer, mechLog?.RPZ),
                        SuctionLine: resolveLogOnlyFieldValue(mechLog?.SuctionLine),
                        Note: resolveLogOnlyFieldValue(mechLog?.Note),
                    };
                    
                    return {
                        ProjectID: projectId, // Use the ID for sorting/display
                        __generalProjectJobStatus: project.JobStatus,
                        MechanicalReferenceRecordID: mechLog?.ID ?? null,
                        MechanicalReferenceHasMatch: Boolean(mechLog),
                        MechanicalReferenceLogRecord: mechLog || null,
                        MechanicalReferenceFieldSources: Object.fromEntries(
                            Object.entries(fieldValues).map(([fieldName, fieldData]) => [fieldName, fieldData.source])
                        ),
                        Engineer: preferExistingValue(project.MechanicalEngineer, mechLog?.MechanicalEngineerInitials),
                        'Engineer Initials': fieldValues.MechanicalEngineerInitials.value,
                        'Engineer Date Entered': fieldValues.MechanicalEngineerDateEntered.value,
                        'Station Type': fieldValues.StationType.value,
                        'Drawing Number': fieldValues.EngineeringDrawingNumber.value,
                        'Drawing Type': project.DrawingType,
                        'Job Number': fieldValues.JobNumber.value,
                        'Job Name': fieldValues.JobName.value,
                        'Station Model Number': fieldValues.StationModelNumber.value,
                        'Flow': fieldValues.Flow.value,
                        'Pressure': fieldValues.Pressure.value,
                        'Voltage': fieldValues.Voltage.value,
                        'Phase': fieldValues.Phase.value,
                        'HC TYPE': fieldValues.HCType.value,
                        'Standard Job': fieldValues.StandarJob.value,
                        'Private Label': fieldValues.PrivateLabel.value,
                        '# of pumps': fieldValues.MainJockeyQty.value,
                        'PUMP ORIENTAION': fieldValues.PumpOrientation.value,
                        'Well Diameter or Length': fieldValues.WetWellDiameterLength.value,
                        'Wet Well Width': fieldValues.WetWellWidth.value,
                        'Wet Well Depth': fieldValues.WetWellDepth.value,
                        'Base length': fieldValues.BaseLength.value,
                        'Base width': fieldValues.BaseWidth.value,
                        'Base Height': fieldValues.BaseHeight.value,
                        'Base Material': fieldValues.BaseMaterial.value,
                        'Piping Material': fieldValues.PipingMaterial.value,
                        'Pipe Color': fieldValues.PipeColor.value,
                        'Station Color': fieldValues.StationColor.value,
                        'Enclosure Type': fieldValues.StationEnclosureType.value,
                        'Panel (L X W X H)': fieldValues.ElectricalPanelSize.value,
                        'Cabinet on/off Skid': fieldValues.PanelOnOffSkid.value,
                        'Station Heater': fieldValues.StationHeater.value,
                        'Transformer Stand': fieldValues.TransformerStand.value,
                        'Main pump Model Number': fieldValues.MainPump.value,
                        'Main Motor (HP)': fieldValues.MainMotorHP.value,
                        'Jockey Pump': fieldValues.JockeyPump.value,
                        'Jockey Motor (HP)': fieldValues.JockeyMotorHP.value,
                        'Pressure Maintenance Pump': fieldValues.SustainPump.value,
                        'Pressure Maintenance Motor (HP)': fieldValues.SustainMotorHP.value,
                        'Water Feature': fieldValues.WaterFeaturePump.value,
                        'Water Feature Motor (HP)': fieldValues.WaterFeatureMotorHP.value,
                        'Transfer Pump': fieldValues.TransferPump.value,
                        'Transfer Motor (HP)': fieldValues.TransferMotorHP.value,
                        'Recirc Pump': fieldValues.RecircPump.value,
                        'Recirc Motor (HP)': fieldValues.RecircMotorHP.value,
                        'Trash Pump': fieldValues.TrashPump.value,
                        'Trash Motor (HP)': fieldValues.TrashMotorHP.value,
                        'Sump Pump': fieldValues.SumpPump.value,
                        'Sump Motor (HP)': fieldValues.SumpMotorHP.value,
                        'Discharge Filter': fieldValues.DischargeFilter.value,
                        '# of Filters': fieldValues.DischargeFilterQty.value,
                        'Filter Orientation': fieldValues.FilterOrientation.value,
                        'Discharge Strainer': fieldValues.DischargeStrainer.value,
                        'Inlet Filtration': fieldValues.InletFiltration.value,
                        'Inlet Strainer': fieldValues.InletStrainer.value,
                        'RO System': fieldValues.ROSystem.value,
                        'UV System': fieldValues.UVSystem.value,
                        'Prv': fieldValues.PRV.value,
                        'EBV': fieldValues.EBV.value,
                        'Flow meter': fieldValues.FlowMeter.value,
                        'Fertigation': fieldValues.Fertigation.value,
                        'SS Fastener': fieldValues.SSFasteners.value,
                        'FBE': fieldValues.FBE.value,
                        'Solidworks': fieldValues.SolidWorks.value,
                        'Water Testing': fieldValues.WaterTesting.value,
                        'Prime': fieldValues.Prime.value,
                        'RPZ': fieldValues.RPZ.value,
                        'Suction Line': fieldValues.SuctionLine.value,
                        'NOTES': fieldValues.Note.value
                    };
                });

                const logOnlyData = latestMechanicalEntries
                    .filter((entry) => {
                        const projectId = entry?.ProjectID;
                        const jobNumber = entry?.JobNumber;
                        const hasProjectId = projectId !== null && projectId !== undefined && projectId !== '';
                        const hasJobNumber = jobNumber !== null && jobNumber !== undefined && jobNumber !== '';

                        if (hasProjectId && existingProjectIds.has(String(projectId))) {
                            return false;
                        }

                        if (hasJobNumber && existingJobNumbers.has(String(jobNumber))) {
                            return false;
                        }

                        return hasProjectId || hasJobNumber;
                    })
                    .map((entry) => ({
                        ProjectID: entry?.ProjectID,
                        __generalProjectJobStatus: null,
                        MechanicalReferenceRecordID: entry?.ID ?? null,
                        MechanicalReferenceHasMatch: true,
                        MechanicalReferenceLogRecord: entry,
                        MechanicalReferenceFieldSources: Object.fromEntries(
                            MECHANICAL_LOG_FIELDS.map((fieldName) => [fieldName, isEmptyValue(entry?.[fieldName]) ? 'empty' : 'log'])
                        ),
                        Engineer: entry?.MechanicalEngineerInitials,
                        'Engineer Initials': entry?.MechanicalEngineerInitials,
                        'Engineer Date Entered': entry?.MechanicalEngineerDateEntered,
                        'Station Type': entry?.StationType,
                        'Drawing Number': entry?.EngineeringDrawingNumber,
                        'Drawing Type': entry?.SolidWorks,
                        'Job Number': entry?.JobNumber,
                        'Job Name': entry?.JobName,
                        'Station Model Number': entry?.StationModelNumber,
                        'Flow': entry?.Flow,
                        'Pressure': entry?.Pressure,
                        'Voltage': entry?.Voltage,
                        'Phase': entry?.Phase,
                        'HC TYPE': entry?.HCType,
                        'Standard Job': entry?.StandarJob,
                        'Private Label': entry?.PrivateLabel,
                        '# of pumps': entry?.MainJockeyQty,
                        'PUMP ORIENTAION': entry?.PumpOrientation,
                        'Well Diameter or Length': entry?.WetWellDiameterLength,
                        'Wet Well Width': entry?.WetWellWidth,
                        'Wet Well Depth': entry?.WetWellDepth,
                        'Base length': entry?.BaseLength,
                        'Base width': entry?.BaseWidth,
                        'Base Height': entry?.BaseHeight,
                        'Base Material': entry?.BaseMaterial,
                        'Piping Material': entry?.PipingMaterial,
                        'Pipe Color': entry?.PipeColor,
                        'Station Color': entry?.StationColor,
                        'Enclosure Type': entry?.StationEnclosureType,
                        'Panel (L X W X H)': entry?.ElectricalPanelSize,
                        'Cabinet on/off Skid': entry?.PanelOnOffSkid,
                        'Station Heater': entry?.StationHeater,
                        'Transformer Stand': entry?.TransformerStand,
                        'Main pump Model Number': entry?.MainPump,
                        'Main Motor (HP)': entry?.MainMotorHP,
                        'Jockey Pump': entry?.JockeyPump,
                        'Jockey Motor (HP)': entry?.JockeyMotorHP,
                        'Pressure Maintenance Pump': entry?.SustainPump,
                        'Pressure Maintenance Motor (HP)': entry?.SustainMotorHP,
                        'Water Feature': entry?.WaterFeaturePump,
                        'Water Feature Motor (HP)': entry?.WaterFeatureMotorHP,
                        'Transfer Pump': entry?.TransferPump,
                        'Transfer Motor (HP)': entry?.TransferMotorHP,
                        'Recirc Pump': entry?.RecircPump,
                        'Recirc Motor (HP)': entry?.RecircMotorHP,
                        'Trash Pump': entry?.TrashPump,
                        'Trash Motor (HP)': entry?.TrashMotorHP,
                        'Sump Pump': entry?.SumpPump,
                        'Sump Motor (HP)': entry?.SumpMotorHP,
                        'Discharge Filter': entry?.DischargeFilter,
                        '# of Filters': entry?.DischargeFilterQty,
                        'Filter Orientation': entry?.FilterOrientation,
                        'Discharge Strainer': entry?.DischargeStrainer,
                        'Inlet Filtration': entry?.InletFiltration,
                        'Inlet Strainer': entry?.InletStrainer,
                        'RO System': entry?.ROSystem,
                        'UV System': entry?.UVSystem,
                        'Prv': entry?.PRV,
                        'EBV': entry?.EBV,
                        'Flow meter': entry?.FlowMeter,
                        'Fertigation': entry?.Fertigation,
                        'SS Fastener': entry?.SSFasteners,
                        'FBE': entry?.FBE,
                        'Solidworks': entry?.SolidWorks,
                        'Water Testing': entry?.WaterTesting,
                        'Prime': entry?.Prime,
                        'RPZ': entry?.RPZ,
                        'Suction Line': entry?.SuctionLine,
                        'NOTES': entry?.Note
                    }));
                
                if (snapshotOnly) return [...combinedData, ...logOnlyData];
                setJobData([...combinedData, ...logOnlyData]);
            } catch (err) {
                if (snapshotOnly) throw err;
                console.error('Error fetching mechanical reference data:', err);
                setError('Failed to load job data. Please try again.');
            } finally {
                if (!snapshotOnly) setLoading(false);
            }
    }, [dispatch, apiUrls]);

    useGuardedRealtimeList({ apiKey: 'generalProject', scopeKey: apiUrls,
        blocked: loading || isSavingCell || Boolean(editingCell.rowKey),
        additionalResources: ['portal:general-station-info', 'portal:pump-details', 'portal:station-enclosure', 'portal:skid-construction', 'portal:pump-manifold-piping', 'portal:electrical-options', 'portal:regulated-power-supply', 'portal:unregulated-power-supply', 'portal:fertigation', 'portal:hc-requirements', 'portal:inlet-screen', 'portal:discharge-filter', 'portal:discharge-strainer', 'portal:inlet-strainer', 'portal:mechanical-reference-log'],
        load: ({ signal }) => fetchAllData({ snapshotOnly: true, signal }), apply: setJobData });

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);
    
    // Get all available columns (excluding Job Number and Job Name which are always shown)
    const availableColumns = useMemo(() => {
        return columnMappings
            .filter(col => col.field && col.column !== 'Job Number' && col.column !== 'Job Name') // Only show mapped columns, exclude sticky columns
            .map(col => col.column);
    }, [columnMappings]);
    
    // Get all fields for filtering (including Job Number and Job Name)
    const allFilterableFields = useMemo(() => {
        return columnMappings
            .filter(col => col.field) // Only mapped fields
            .map(col => col.column)
            .sort();
    }, [columnMappings]);
    
    // Get unique values for a specific field
    const getUniqueValuesForField = useCallback((fieldName) => {
        const values = jobData
            .map(job => job[fieldName])
            .filter(val => val !== null && val !== undefined && val !== '')
            .map(val => val.toString());
        return [...new Set(values)].sort();
    }, [jobData]);
    
    // Advanced filter handlers
    const addFilter = () => {
        setAdvancedFilters(prev => [...prev, { id: nextFilterId, field: '', value: '', fieldSearch: '' }]);
        setNextFilterId(prev => prev + 1);
    };
    
    const removeFilter = (id) => {
        setAdvancedFilters(prev => prev.filter(f => f.id !== id));
    };
    
    const updateFilter = (id, updates) => {
        setAdvancedFilters(prev => prev.map(f => 
            f.id === id ? { ...f, ...updates } : f
        ));
    };
    
    const clearAllFilters = () => {
        setAdvancedFilters([]);
        setSearchTerm('');
        setShowShippedOnly(false);
    };

    const getRowKey = (job, index) => `${job.ProjectID ?? 'no-project'}-${job.MechanicalReferenceRecordID ?? 'no-record'}-${index}`;

    const closeInlineCellEditor = () => {
        if (isSavingCell) {
            return;
        }

        setEditingCell({ rowKey: null, fieldName: null });
        setEditingJob(null);
        setEditValue('');
        setOriginalValue('');
        setInlineEditError('');
    };

    const canEditColumnForJob = (job, columnKey) => {
        const fieldName = TABLE_KEY_TO_LOG_FIELD[columnKey];
        if (!fieldName) {
            return false;
        }

        return isFieldEditableForJob(job, fieldName);
    };

    const handleCellDoubleClick = (event, job, index, columnKey) => {
        event.stopPropagation();

        const fieldName = TABLE_KEY_TO_LOG_FIELD[columnKey];
        if (!fieldName || !canEditColumnForJob(job, columnKey)) {
            return;
        }

        const rowKey = getRowKey(job, index);
        const currentValue = getContextFieldValue(job, fieldName);

        setEditingCell({ rowKey, fieldName });
        setEditingJob(job);
        setEditValue(currentValue ?? '');
        setOriginalValue(currentValue ?? '');
        setInlineEditError('');
    };

    const validateFieldValue = (job, fieldName, value) => {
        const hasProjectId = !isEmptyValue(job?.ProjectID);
        const hasJobNumber = !isEmptyValue(job?.['Job Number']);

        if (!hasProjectId && !hasJobNumber) {
            return 'ProjectID or Job Number is required to save a Mechanical Reference Log record.';
        }

        if (MECHANICAL_LOG_DATE_FIELDS.has(fieldName) && !isEmptyValue(value) && !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
            return `${getFieldLabel(fieldName)} must be in YYYY-MM-DD format.`;
        }

        if (MECHANICAL_LOG_NUMERIC_FIELDS.has(fieldName) && !isEmptyValue(value) && Number.isNaN(Number(value))) {
            return `${getFieldLabel(fieldName)} must be numeric.`;
        }

        return '';
    };

    const handleSaveCellEdit = async () => {
        if (!editingJob || !editingCell.fieldName) {
            return;
        }

        const fieldName = editingCell.fieldName;
        const nextValue = isEmptyValue(editValue) ? null : editValue;
        if (arePatchValuesEquivalent(nextValue, originalValue)) {
            closeInlineCellEditor();
            return;
        }

        const validationError = validateFieldValue(editingJob, fieldName, nextValue);
        if (validationError) {
            setInlineEditError(validationError);
            return;
        }

        setIsSavingCell(true);
        setInlineEditError('');

        try {
            if (editingJob?.MechanicalReferenceHasMatch) {
                const baselineRecord = editingJob?.MechanicalReferenceLogRecord || {};
                const patchPayload = {
                    ...(editingJob?.MechanicalReferenceRecordID
                        ? { ID: editingJob.MechanicalReferenceRecordID }
                        : {}),
                    ProjectID: isEmptyValue(editingJob?.ProjectID) ? null : editingJob.ProjectID,
                };

                if (!arePatchValuesEquivalent(nextValue, baselineRecord[fieldName])) {
                    patchPayload[fieldName] = nextValue;
                }

                const changedFields = Object.keys(patchPayload).filter((fieldName) => fieldName !== 'ID' && fieldName !== 'ProjectID');
                if (changedFields.length > 0) {
                    await dispatch(patchData({
                        url: apiUrls.mechanicalReferenceLog,
                        data: patchPayload,
                        allowFullPayload: true,
                    })).unwrap();
                }

                setJobData((previousRows) => previousRows.map((row) => {
                    if (row !== editingJob) {
                        return row;
                    }

                    const updatedLogRecord = {
                        ...(row.MechanicalReferenceLogRecord || {}),
                        [fieldName]: nextValue,
                    };

                    return {
                        ...row,
                        [LOG_FIELD_TO_TABLE_KEY[fieldName]]: nextValue,
                        MechanicalReferenceLogRecord: updatedLogRecord,
                        MechanicalReferenceFieldSources: {
                            ...(row.MechanicalReferenceFieldSources || {}),
                            [fieldName]: isEmptyValue(nextValue) ? 'empty' : 'log',
                        },
                    };
                }));
            } else {
                const postPayload = {
                    ProjectID: isEmptyValue(editingJob?.ProjectID) ? null : editingJob.ProjectID,
                    JobNumber: editingJob?.['Job Number'] || null,
                    JobName: editingJob?.['Job Name'] || null,
                    [fieldName]: nextValue,
                };

                const createdRecord = await dispatch(postData({
                    url: apiUrls.mechanicalReferenceLog,
                    data: postPayload,
                    contract: { requestCase: 'raw' },
                })).unwrap();

                const createdRecordId = createdRecord?.ID ?? createdRecord?.id ?? null;
                setJobData((previousRows) => previousRows.map((row) => {
                    if (row !== editingJob) {
                        return row;
                    }

                    const updatedLogRecord = {
                        ...(row.MechanicalReferenceLogRecord || {}),
                        ...(createdRecord && typeof createdRecord === 'object' ? createdRecord : {}),
                        ProjectID: isEmptyValue(row.ProjectID) ? null : row.ProjectID,
                        JobNumber: row['Job Number'] || null,
                        JobName: row['Job Name'] || null,
                        [fieldName]: nextValue,
                    };

                    return {
                        ...row,
                        [LOG_FIELD_TO_TABLE_KEY[fieldName]]: nextValue,
                        MechanicalReferenceHasMatch: true,
                        MechanicalReferenceRecordID: createdRecordId,
                        MechanicalReferenceLogRecord: updatedLogRecord,
                        MechanicalReferenceFieldSources: {
                            ...(row.MechanicalReferenceFieldSources || {}),
                            [fieldName]: isEmptyValue(nextValue) ? 'empty' : 'log',
                        },
                    };
                }));
            }

            closeInlineCellEditor();
        } catch (err) {
            console.error('Error saving mechanical reference log cell:', err);
            setInlineEditError(err?.message || 'Failed to save mechanical reference log field.');
        } finally {
            setIsSavingCell(false);
        }
    };

    const handleEditInputKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeInlineCellEditor();
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSaveCellEdit();
        }
    };

    
    
    // Initialize selected columns with most important ones (Job Number and Job Name are always shown)
    useEffect(() => {
        if (selectedColumns.length === 0) {
            setSelectedColumns([
                'Engineer', 'Drawing Number', 'Station Type', 'Flow', 
                'Pressure', 'Voltage', 'Main pump Model Number', 'Main Motor (HP)'
            ]);
        }
    }, [selectedColumns.length]);
    
    // Filter and sort jobs based on search and sort config
    const filteredAndSortedJobs = useMemo(() => {
        let filtered = jobData;

        // Job status is intentionally sourced only from GeneralProjectInfo.
        if (showShippedOnly) {
            filtered = filtered.filter(hasShippedGeneralProjectStatus);
        }
        
        // Apply global search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(job => {
                return Object.entries(job).some(([key, value]) =>
                    !key.startsWith('__') &&
                    value?.toString().toLowerCase().includes(search)
                );
            });
        }
        
        // Apply advanced filters (AND logic)
        if (advancedFilters.length > 0) {
            filtered = filtered.filter(job => {
                return advancedFilters.every(filter => {
                    if (!filter.field || !filter.value) return true; // Skip incomplete filters
                    
                    const jobValue = job[filter.field];
                    if (jobValue === null || jobValue === undefined) return false;
                    
                    return jobValue.toString().toLowerCase().includes(filter.value.toLowerCase());
                });
            });
        }
        
        // Apply sorting
        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                
                // Handle null/undefined values
                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;
                
                // Numeric comparison if both values are numbers or can be parsed as numbers
                const aNum = Number(aValue);
                const bNum = Number(bValue);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                }
                
                // String comparison
                const aStr = aValue.toString().toLowerCase();
                const bStr = bValue.toString().toLowerCase();
                if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        return filtered;
    }, [jobData, searchTerm, sortConfig, advancedFilters, showShippedOnly]);
    
    // Paginate results
    const paginatedJobs = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredAndSortedJobs.slice(startIndex, endIndex);
    }, [filteredAndSortedJobs, currentPage, itemsPerPage]);
    
    // Calculate total pages
    const totalPages = Math.ceil(filteredAndSortedJobs.length / itemsPerPage);
    
    // Toggle column selection
    const toggleColumn = (column) => {
        setSelectedColumns(prev => 
            prev.includes(column) 
                ? prev.filter(c => c !== column)
                : [...prev, column]
        );
    };
    
    // Handle column sort
    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
        setCurrentPage(1); // Reset to first page when sorting
    };
    
    // Handle page change
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // Scroll to top of table
        document.querySelector('.table-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    
    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedColumns, advancedFilters, showShippedOnly]);
    
    // Select/Deselect all columns
    const handleSelectAll = () => {
        setSelectedColumns(availableColumns);
    };
    
    const handleDeselectAll = () => {
        setSelectedColumns([]);
    };

    
    // Export to CSV (always include Job Number and Job Name)
    const handleExport = () => {
        const headers = ['Job Number', 'Job Name', ...selectedColumns];
        const csvContent = [
            headers,
            ...filteredAndSortedJobs.map(job => 
                headers.map(header => job[header] || '')
            )
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `mechanical-reference-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    if (loading) {
        return (
            <div className="mechanical-reference-container">
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Loading job data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mechanical-reference-container">
                <Alert variant="danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </Alert>
            </div>
        );
    }

    return (
        <div className="mechanical-reference-container">
            <div className="header-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h2 className="mb-1">Mechanical Reference Data</h2>
                        <p className="text-muted mb-0">Comprehensive job specifications across all mechanical fields</p>
                    </div>
                    <button 
                        className="btn btn-outline-primary"
                        onClick={handleExport}
                        disabled={filteredAndSortedJobs.length === 0 || selectedColumns.length === 0}
                    >
                        <i className="bi bi-download me-2"></i>
                        Export CSV
                    </button>
                </div>

                {/* Compact Statistics */}
                <div className="compact-stats mb-3">
                    <span className="stat-item">
                        <strong>{jobData.length}</strong> Total Jobs
                    </span>
                    <span className="stat-divider">•</span>
                    <span className="stat-item">
                        <strong>{filteredAndSortedJobs.length}</strong> Filtered
                    </span>
                    <span className="stat-divider">•</span>
                    <span className="stat-item">
                        <strong>{availableColumns.length}</strong> Available Fields
                    </span>
                    <span className="stat-divider">•</span>
                    <span className="stat-item">
                        <strong>{selectedColumns.length}</strong> Selected
                    </span>
                </div>

                {/* Advanced Filters */}
                <div className="advanced-filters-section mb-3">
                    <div className="mechanical-reference-filter-toolbar d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                            <i className="bi bi-funnel me-2"></i>
                            Advanced Filters
                        </h6>
                        <div className="mechanical-reference-filter-actions d-flex flex-wrap align-items-center gap-3">
                            <Form.Check
                                type="switch"
                                id="mechanical-reference-shipped-only"
                                label="Shipped jobs only"
                                checked={showShippedOnly}
                                onChange={(event) => setShowShippedOnly(event.target.checked)}
                                title="Show jobs with a Shipped status in General Project"
                            />
                            {(advancedFilters.length > 0 || searchTerm || showShippedOnly) && (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={clearAllFilters}
                                >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Clear All
                                </button>
                            )}
                            <button
                                className="btn btn-sm btn-success"
                                onClick={addFilter}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                Add Filter
                            </button>
                        </div>
                    </div>
                    
                    {advancedFilters.length > 0 && (
                        <div className="filters-list">
                            {advancedFilters.map((filter, index) => (
                                <AdvancedFilterRow
                                    key={filter.id}
                                    filter={filter}
                                    index={index}
                                    allFields={allFilterableFields}
                                    getUniqueValues={getUniqueValuesForField}
                                    onUpdate={(updates) => updateFilter(filter.id, updates)}
                                    onRemove={() => removeFilter(filter.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Search and Display Controls */}
                <div className="filter-section">
                    <Row className="g-3 mb-3">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <i className="bi bi-search"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search across all job data..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3} className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline-success flex-fill"
                                onClick={handleSelectAll}
                                title="Select all columns"
                            >
                                <i className="bi bi-check-all"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-danger flex-fill"
                                onClick={handleDeselectAll}
                                title="Deselect all columns"
                            >
                                <i className="bi bi-x-circle"></i>
                            </button>
                        </Col>
                        <Col md={3} className="d-flex align-items-center gap-2">
                            <Form.Check
                                type="switch"
                                id="compact-view"
                                label="Compact"
                                checked={compactView}
                                onChange={(e) => setCompactView(e.target.checked)}
                                title="Toggle compact view"
                            />
                            <Form.Select
                                size="sm"
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                style={{ width: 'auto' }}
                                title="Items per page"
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={500}>500</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    
                    {/* Column Selection */}
                    <div className="column-selector">
                        <strong className="d-block mb-2">
                            <i className="bi bi-columns-gap me-2"></i>
                            Select Columns to Display:
                        </strong>
                        <div className="column-pills">
                            {availableColumns.map(column => (
                                <Badge
                                    key={column}
                                    bg={selectedColumns.includes(column) ? 'primary' : 'secondary'}
                                    className="column-pill"
                                    onClick={() => toggleColumn(column)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {selectedColumns.includes(column) && <i className="bi bi-check2 me-1"></i>}
                                    {column}
                                </Badge>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Results Info and Pagination */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="results-info">
                    <span className="text-muted">
                        Showing <strong>{paginatedJobs.length}</strong> of <strong>{filteredAndSortedJobs.length}</strong> jobs
                        {searchTerm && <> (filtered from <strong>{jobData.length}</strong> total)</>}
                        {' '}• <strong>{selectedColumns.length}</strong> columns selected
                    </span>
                </div>
                
                {totalPages > 1 && (
                    <div className="pagination-controls">
                        <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="text-muted">
                            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </span>
                        <button
                            className="btn btn-sm btn-outline-primary ms-2"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                )}
            </div>

            {inlineEditError && (
                <Alert variant="danger" className="py-2">
                    {inlineEditError}
                </Alert>
            )}

            {/* Main Data Table */}
            {selectedColumns.length === 0 ? (
                <Alert variant="warning" className="text-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Please select at least one column to display data
                </Alert>
            ) : filteredAndSortedJobs.length === 0 ? (
                <Alert variant="info" className="text-center">
                    <i className="bi bi-info-circle me-2"></i>
                    No jobs match your current search criteria
                </Alert>
            ) : (
                <div className="table-container">
                    <Table 
                        striped 
                        bordered 
                        hover 
                        responsive 
                        className={`mechanical-reference-table ${compactView ? 'compact-table' : ''}`}
                    >
                        <thead className="table-header">
                            <tr>
                                <th 
                                    style={{ minWidth: '120px', position: 'sticky', left: 0, zIndex: 10, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    onClick={() => handleSort('Job Number')}
                                    title="Click to sort"
                                >
                                    Job Number
                                    {sortConfig.key === 'Job Number' && (
                                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                </th>
                                <th 
                                    style={{ minWidth: '200px', position: 'sticky', left: '120px', zIndex: 10, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    onClick={() => handleSort('Job Name')}
                                    title="Click to sort"
                                >
                                    Job Name
                                    {sortConfig.key === 'Job Name' && (
                                        <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                    )}
                                </th>
                                {selectedColumns.map(column => (
                                    <th 
                                        key={column} 
                                        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => handleSort(column)}
                                        title="Click to sort"
                                    >
                                        {column}
                                        {sortConfig.key === column && (
                                            <i className={`bi bi-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedJobs.map((job, index) => {
                                const rowKey = getRowKey(job, index);

                                const renderEditableCell = (columnKey, cellStyle = {}) => {
                                    const fieldName = TABLE_KEY_TO_LOG_FIELD[columnKey];
                                    const canEdit = canEditColumnForJob(job, columnKey);
                                    const isEditing = editingCell.rowKey === rowKey && editingCell.fieldName === fieldName;

                                    if (isEditing && fieldName) {
                                        const isDateField = MECHANICAL_LOG_DATE_FIELDS.has(fieldName);
                                        const isNoteField = fieldName === 'Note';

                                        return (
                                            <td key={`${rowKey}-${columnKey}`} style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: '160px' }}>
                                                {isNoteField ? (
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={editValue ?? ''}
                                                        onChange={(event) => setEditValue(event.target.value)}
                                                        onBlur={handleSaveCellEdit}
                                                        onKeyDown={handleEditInputKeyDown}
                                                        autoFocus
                                                        disabled={isSavingCell}
                                                    />
                                                ) : (
                                                    <Form.Control
                                                        type={isDateField ? 'date' : 'text'}
                                                        value={editValue ?? ''}
                                                        onChange={(event) => setEditValue(event.target.value)}
                                                        onBlur={handleSaveCellEdit}
                                                        onKeyDown={handleEditInputKeyDown}
                                                        autoFocus
                                                        disabled={isSavingCell}
                                                    />
                                                )}
                                            </td>
                                        );
                                    }

                                    return (
                                        <td
                                            key={`${rowKey}-${columnKey}`}
                                            style={{ ...cellStyle, whiteSpace: 'nowrap', cursor: canEdit ? 'pointer' : 'default' }}
                                            onDoubleClick={(event) => handleCellDoubleClick(event, job, index, columnKey)}
                                            title={canEdit ? 'Double-click to edit' : undefined}
                                        >
                                            {job[columnKey] !== null && job[columnKey] !== undefined
                                                ? job[columnKey].toString()
                                                : <span className="text-muted">—</span>
                                            }
                                        </td>
                                    );
                                };

                                return (
                                    <tr key={rowKey}>
                                        {renderEditableCell('Job Number', { position: 'sticky', left: 0, backgroundColor: 'white', fontWeight: 'bold' })}
                                        {renderEditableCell('Job Name', { position: 'sticky', left: '120px', backgroundColor: 'white' })}
                                        {selectedColumns.map((column) => renderEditableCell(column))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
            
            {/* Bottom Pagination */}
            {totalPages > 1 && filteredAndSortedJobs.length > 0 && (
                <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                    >
                        <i className="bi bi-chevron-double-left"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="bi bi-chevron-left"></i> Previous
                    </button>
                    <span className="text-muted">
                        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                    </span>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next <i className="bi bi-chevron-right"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="bi bi-chevron-double-right"></i>
                    </button>
                </div>
            )}

            {/* Footer Help Text */}
            <div className="footer-info mt-4">
                <Alert variant="light" className="mb-0">
                    <div className="d-flex align-items-start">
                        <i className="bi bi-lightbulb text-warning me-3 fs-5"></i>
                        <div>
                            <strong>Usage Guide:</strong>
                            <ul className="mb-0 mt-2">
                                <li><strong>Search:</strong> Type to search across all visible job data</li>
                                <li><strong>Column Selection:</strong> Click column badges to show/hide specific fields</li>
                                <li><strong>Sorting:</strong> Click any column header to sort ascending/descending</li>
                                <li><strong>Pagination:</strong> Use page controls or change items per page (25/50/100/500)</li>
                                <li><strong>Compact View:</strong> Toggle for denser table layout with smaller text</li>
                                <li><strong>Sticky Columns:</strong> Job Number and Job Name stay visible while scrolling horizontally</li>
                                <li><strong>Inline Edit:</strong> Double-click editable cells to update mechanical log data (PATCH existing or POST new)</li>
                                <li><strong>Export:</strong> Download all filtered/sorted results with selected columns as CSV</li>
                            </ul>
                        </div>
                    </div>
                </Alert>
            </div>
        </div>
    );
};

export default MechanicalReference;
